<script setup lang="ts">
const props = defineProps<{
  name: string;
  logo: string | null;
  coverImage: string | null;
  // Bannière de secours (cagnotte la plus dotée) si pas de coverImage.
  fallbackBanner?: string | null;
}>();

const config = useRuntimeConfig();
const assetsBaseUrl = config.public.apiUrl;

const logoUrl = computed(() =>
  props.logo
    ? `${assetsBaseUrl}/api/assets/${props.logo}?width=200&height=200&fit=cover&format=webp&quality=80`
    : null
);

const coverUrl = computed(() => {
  // Cascade : cover_image de l'asso, sinon bannière de la cagnotte la plus dotée.
  const src = props.coverImage || props.fallbackBanner;
  return src
    ? `${assetsBaseUrl}/api/assets/${src}?width=1200&height=400&fit=cover&format=webp&quality=80`
    : null;
});

const initial = computed(() => props.name.charAt(0).toUpperCase());

const { t } = useI18n();
</script>

<template>
  <div class="flex items-center justify-center min-h-screen px-4 py-10">
    <div
      class="w-full max-w-md bg-white rounded-3xl border border-gray-200 shadow-[0_2px_8px_rgba(0,0,0,0.04),0_4px_24px_rgba(0,0,0,0.06)] overflow-hidden"
    >
      <!-- Cover -->
      <div
        class="relative h-32 bg-gradient-to-br from-gray-100 to-gray-200 overflow-hidden"
      >
        <img
          v-if="coverUrl"
          :src="coverUrl"
          :alt="name"
          class="absolute inset-0 w-full h-full object-cover"
        />
        <div
          class="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"
        />
      </div>

      <div class="px-6 pb-7 -mt-12 relative flex flex-col items-center text-center">
        <!-- Logo -->
        <div
          class="size-24 rounded-2xl bg-white ring-4 ring-white shadow-md overflow-hidden flex items-center justify-center"
        >
          <img
            v-if="logoUrl"
            :src="logoUrl"
            :alt="name"
            class="w-full h-full object-cover"
          />
          <span v-else class="text-3xl font-bold text-gray-400">{{
            initial
          }}</span>
        </div>

        <!-- Name -->
        <h1 class="mt-5 text-xl font-bold text-gray-900 leading-tight">
          {{ name }}
        </h1>

        <!-- Status pill -->
        <div
          class="mt-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 text-xs font-semibold ring-1 ring-amber-200"
        >
          <UIcon name="i-lucide-clock" class="size-3" />
          <span>{{ t("association.incomplete.badge") }}</span>
        </div>

        <!-- Message -->
        <p class="mt-4 text-sm text-gray-600 leading-relaxed">
          {{ t("association.incomplete.message") }}
        </p>

        <!-- Actions -->
        <div class="mt-6 flex flex-col gap-2.5 w-full">
          <a
            href="https://donoor.org/recherche/associations"
            class="inline-flex items-center justify-center gap-2 w-full py-3 rounded-full text-sm font-semibold bg-main text-white hover:bg-main/90 transition-colors"
          >
            <UIcon name="i-lucide-search" class="size-4" />
            {{ t("association.incomplete.explorerAutres") }}
          </a>
          <a
            href="https://donoor.org"
            class="inline-flex items-center justify-center gap-2 w-full py-3 rounded-full text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
          >
            <UIcon name="i-lucide-home" class="size-4" />
            {{ t("association.incomplete.retourAccueil") }}
          </a>
        </div>
      </div>
    </div>
  </div>
</template>
